import { BlockDataTypeEnum } from '@naturehouse/design-system/components/atoms/period/models/AbstractPeriodModel';
import HttpClient from '@naturehouse/nh-essentials/lib/requests/HttpClient';
import type HttpError from '@naturehouse/nh-essentials/lib/requests/HttpError';
import calendarEvents from '../../../events/calendarEvents';
import { formatDateForUrl, isBetweenDates } from '../../../util/dateHelper';
import { FeatureToggle, isFeatureEnabled } from '../../../util/featureToggle';
import PeriodBlockCollection from '../../periodCalendar/PeriodBlockCollection';
import PeriodConfigurationCollection from '../../periodCalendar/PeriodConfigurationCollection';

/**
 * @Deprecated
 *
 * The implementation is outdated and does not align with our newer architectural decisions and standards.
 * This class is partly being transitioning away to more efficient, scalable and maintainable solutions.
 * Starting with https://natuurhuisje.atlassian.net/browse/LUM-9497
 */
class PeriodStore {
    private readonly _state: PeriodState = {
        periods: [],
        blockedDates: [],
        bookedPeriods: []
    };

    private houseId: string | null = null;

    private readonly blocksApiUrl = `${window.location.origin}/api/houses/{{houseId}}/blocks`;

    private readonly periodsApiUrl = `${window.location.origin}/api/houses/{{houseId}}/periods`;

    private readonly bookedPeriodsApiUrl = `${window.location.origin}/api/houses/{{houseId}}/booked_periods`;

    readonly #periodBlockCollection = PeriodBlockCollection.getInstance();

    readonly #periodConfigurationCollection = PeriodConfigurationCollection.getInstance();

    public constructor(initialState: PeriodState | undefined = undefined) {
        if (initialState) {
            this._state = { ...this._state, ...initialState };
        }
    }

    public setHouseId(houseId: string): Promise<void> {
        this.houseId = houseId || null;
        return this.refresh();
    }

    get state(): PeriodState {
        return this._state;
    }

    public async addBlock(block: BlockedPeriod): Promise<Response | void> {
        if (this.houseId === null) {
            return Promise.resolve();
        }

        if (isFeatureEnabled(FeatureToggle.NEW_CALENDAR)) {
            try {
                await this.#periodBlockCollection.add(
                    {
                        start: block.start,
                        end: block.end,
                        data: {
                            reason: block.reason ?? '',
                            type: block.editable
                                ? BlockDataTypeEnum.MANUAL_BLOCK
                                : BlockDataTypeEnum.SYNCED_BLOCK
                        }
                    },
                    {
                        houseId: this.houseId
                    }
                );
            } catch (e: unknown) {
                return Promise.reject(e);
            }

            return Promise.resolve();
        }

        const endpoint: string = this.blocksApiUrl.replace('{{houseId}}', this.houseId.toString());
        const data: string = JSON.stringify({
            start: block.start,
            end: block.end,
            reason: block.reason
        });
        this._state.blockedDates.push(block);
        document.dispatchEvent(new Event(calendarEvents.calendarUpdated));

        return HttpClient.post(endpoint, data)
            .then((response: unknown) => {
                const index: number = this._state.blockedDates.indexOf(block);
                const period: BlockedPeriod = response as BlockedPeriod;
                this._state.blockedDates[index] = period;
                document.dispatchEvent(
                    new CustomEvent(calendarEvents.blockAdded, {
                        detail: period
                    })
                );
            })
            .catch((response: HttpError<ErrorResponse>) => {
                const index: number = this._state.blockedDates.indexOf(block);
                this._state.blockedDates.splice(index, 1);
                document.dispatchEvent(
                    new CustomEvent(calendarEvents.blockAddFailed, {
                        detail: block
                    })
                );

                throw new Error(response.body?.error ?? 'Failed to add block');
            });
    }

    public async updateBlock(block: BlockedPeriod): Promise<Response | void> {
        if (this.houseId === null || !Array.isArray(this._state.blockedDates)) {
            return Promise.resolve();
        }

        if (isFeatureEnabled(FeatureToggle.NEW_CALENDAR)) {
            try {
                await this.#periodBlockCollection.update(
                    {
                        start: block.start,
                        end: block.end,
                        data: {
                            reason: block.reason ?? '',
                            type: block.editable
                                ? BlockDataTypeEnum.MANUAL_BLOCK
                                : BlockDataTypeEnum.SYNCED_BLOCK
                        }
                    },
                    {
                        houseId: this.houseId,
                        blockId: block.id.toString()
                    }
                );
            } catch (e: unknown) {
                return Promise.reject(e);
            }

            return Promise.resolve();
        }

        const index: number = this._state.blockedDates.findIndex(
            (item: BlockedPeriod) => Number(item.id) === block.id
        );
        const originalPeriod = this._state.blockedDates[index];

        if (index === -1) {
            return Promise.resolve();
        }

        this._state.blockedDates[index] = block;
        document.dispatchEvent(new Event(calendarEvents.calendarUpdated));

        const data: StandardObjectInterface = {
            start: block.start,
            end: block.end,
            reason: block.reason
        };

        const endpoint: string = this.blocksApiUrl.replace('{{houseId}}', this.houseId.toString());
        return HttpClient.put(`${endpoint}/${block.id}`, JSON.stringify(data))
            .then((response: unknown) => {
                const updatedBlock: BlockedPeriod = response as BlockedPeriod;
                document.dispatchEvent(
                    new CustomEvent(calendarEvents.blockUpdated, {
                        detail: updatedBlock
                    })
                );
            })
            .catch((response: HttpError<ErrorResponse>) => {
                this._state.blockedDates[index] = originalPeriod;
                document.dispatchEvent(
                    new CustomEvent(calendarEvents.blockUpdateFailed, {
                        detail: block
                    })
                );

                throw new Error(response.body?.error ?? 'Failed to update block');
            });
    }

    public async deleteBlock(id: number): Promise<Response | void> {
        if (this.houseId === null) {
            return Promise.resolve();
        }

        if (isFeatureEnabled(FeatureToggle.NEW_CALENDAR)) {
            try {
                await this.#periodBlockCollection.delete({
                    houseId: this.houseId,
                    blockId: id.toString()
                });
            } catch (e: unknown) {
                return Promise.reject(e);
            }

            return Promise.resolve();
        }

        const index: number = this._state.blockedDates.findIndex(
            (item: BlockedPeriod) => Number(item.id) === id
        );

        if (index === -1) {
            return Promise.resolve();
        }

        const endpoint: string = this.blocksApiUrl.replace('{{houseId}}', this.houseId.toString());
        const block: BlockedPeriod = this._state.blockedDates.splice(index, 1)[0];
        document.dispatchEvent(new Event(calendarEvents.calendarUpdated));

        return HttpClient.delete(`${endpoint}/${id}`)
            .then(() => {
                document.dispatchEvent(
                    new CustomEvent(calendarEvents.blockDeleted, {
                        detail: block
                    })
                );
            })
            .catch((response: HttpError<ErrorResponse>) => {
                this._state.blockedDates.splice(index, 0, block);
                document.dispatchEvent(
                    new CustomEvent(calendarEvents.blockDeleteFailed, {
                        detail: block
                    })
                );

                throw new Error(response.body?.error ?? 'Failed to delete block');
            });
    }

    public getBlockByDate(date: Date): BlockedPeriod | undefined {
        return this._state.blockedDates.find((block: BlockedPeriod) => {
            if (isBetweenDates(date, new Date(block.start), new Date(block.end))) {
                return block;
            }

            return null;
        });
    }

    public getPeriodByDate(date: Date): Period | undefined {
        return this._state.periods.find((period: Period) => {
            if (isBetweenDates(date, new Date(period.start), new Date(period.end))) {
                return period;
            }

            return null;
        });
    }

    public getBookedPeriodByDate(date: Date): BookedPeriod | undefined {
        return this._state.bookedPeriods.find((period: BookedPeriod) => {
            if (isBetweenDates(date, new Date(period.start), new Date(period.end))) {
                return period;
            }

            return null;
        });
    }

    public async addPeriod(period: Period): Promise<Response | void> {
        if (this.houseId === null) {
            return Promise.resolve();
        }

        if (isFeatureEnabled(FeatureToggle.NEW_CALENDAR)) {
            try {
                await this.#periodConfigurationCollection.add(
                    {
                        start: period.start,
                        end: period.end,
                        data: {
                            pricing: period.pricing,
                            availability: period.availability
                        }
                    },
                    {
                        houseId: this.houseId
                    }
                );
            } catch (e: unknown) {
                return Promise.reject(e);
            }

            return Promise.resolve();
        }

        this._state.periods.push(period);
        document.dispatchEvent(new Event(calendarEvents.calendarUpdated));

        const data = {
            start: period.start,
            end: period.end,
            pricing: period.pricing,
            availability: period.availability
        };

        const endpoint: string = this.periodsApiUrl.replace('{{houseId}}', this.houseId.toString());
        return HttpClient.post(endpoint, JSON.stringify(data))
            .then((response: unknown) => {
                const index: number = this._state.periods.indexOf(period);
                const updatedPeriod: Period = response as Period;
                this._state.periods[index] = updatedPeriod;
                document.dispatchEvent(
                    new CustomEvent(calendarEvents.periodAdded, {
                        detail: updatedPeriod
                    })
                );
            })
            .catch((response: HttpError<ErrorResponse>) => {
                const index: number = this._state.periods.indexOf(period);
                this._state.periods.splice(index, 1);
                document.dispatchEvent(
                    new CustomEvent(calendarEvents.periodAddFailed, {
                        detail: period
                    })
                );

                throw new Error(response.body?.error ?? 'Failed to add period');
            });
    }

    public async updatePeriod(period: Period): Promise<Response | void> {
        if (this.houseId === null) {
            return Promise.resolve();
        }

        if (isFeatureEnabled(FeatureToggle.NEW_CALENDAR)) {
            try {
                await this.#periodConfigurationCollection.update(
                    {
                        start: period.start,
                        end: period.end,
                        data: {
                            pricing: period.pricing,
                            availability: period.availability
                        }
                    },
                    {
                        houseId: this.houseId,
                        blockId: period.id.toString()
                    }
                );
            } catch (e: unknown) {
                return Promise.reject(e);
            }

            return Promise.resolve();
        }

        const index: number = this._state.periods.findIndex(
            (item: Period) => Number(item.id) === period.id
        );
        const originalPeriod = this._state.periods[index];

        if (index === -1) {
            return Promise.resolve();
        }

        this._state.periods[index] = period;
        document.dispatchEvent(new Event(calendarEvents.calendarUpdated));

        const data = {
            start: period.start,
            end: period.end,
            pricing: period.pricing,
            availability: period.availability
        };

        const endpoint: string = this.periodsApiUrl.replace('{{houseId}}', this.houseId.toString());
        return HttpClient.put(`${endpoint}/${period.id}`, JSON.stringify(data))
            .then((data: unknown) => {
                document.dispatchEvent(
                    new CustomEvent(calendarEvents.periodUpdated, {
                        detail: data as Period
                    })
                );
            })
            .catch((response: HttpError<ErrorResponse>) => {
                this._state.periods[index] = originalPeriod;
                document.dispatchEvent(
                    new CustomEvent(calendarEvents.periodUpdateFailed, {
                        detail: period
                    })
                );

                throw new Error(response.body?.error ?? 'Failed to update period');
            });
    }

    public async deletePeriod(id: number): Promise<Response | void> {
        if (this.houseId === null) {
            return Promise.resolve();
        }

        if (isFeatureEnabled(FeatureToggle.NEW_CALENDAR)) {
            try {
                await this.#periodConfigurationCollection.delete({
                    houseId: this.houseId,
                    blockId: id.toString()
                });
            } catch (e: unknown) {
                return Promise.reject(e);
            }

            return Promise.resolve();
        }

        const index: number = this._state.periods.findIndex(
            (item: Period) => Number(item.id) === id
        );

        if (index === -1) {
            return Promise.resolve();
        }

        const endpoint: string = this.periodsApiUrl.replace('{{houseId}}', this.houseId.toString());
        const period: Period = this._state.periods.splice(index, 1)[0];
        document.dispatchEvent(new Event(calendarEvents.calendarUpdated));

        return HttpClient.delete(`${endpoint}/${id}`)
            .then(() => {
                document.dispatchEvent(
                    new CustomEvent(calendarEvents.periodDeleted, {
                        detail: period
                    })
                );
            })
            .catch((response: HttpError<ErrorResponse>) => {
                this._state.periods.splice(index, 0, period);
                document.dispatchEvent(
                    new CustomEvent(calendarEvents.periodDeleteFailed, {
                        detail: period
                    })
                );

                throw new Error(response.body?.error ?? 'Failed to delete period');
            });
    }

    public fetchBlocks(start: Date | null = null, end: Date | null = null): Promise<any> {
        if (this.houseId === null) {
            return Promise.resolve();
        }

        let endpoint: string = this.blocksApiUrl.replace('{{houseId}}', this.houseId.toString());

        const urlParams: string = this.formatUrlParams(start, end);
        if (urlParams.length) {
            endpoint += `?${urlParams}`;
        }

        return HttpClient.get(endpoint).then((response: unknown) => {
            const blocks: BlockedPeriod[] = response as BlockedPeriod[];

            if (
                !Array.isArray(blocks) ||
                (blocks.length === 0 && this._state.blockedDates.length === 0)
            ) {
                return;
            }

            this._state.blockedDates = this._state.blockedDates.filter(
                (block: BlockedPeriod): boolean =>
                    blocks.findIndex(
                        (newBlock: BlockedPeriod): boolean => newBlock.id === block.id
                    ) === -1
            );
            this._state.blockedDates.push(...blocks);
            document.dispatchEvent(new Event(calendarEvents.calendarUpdated));
        });
    }

    public fetchPeriods(start: Date | null = null, end: Date | null = null): Promise<any> {
        if (this.houseId === null) {
            return Promise.resolve();
        }

        let endpoint: string = this.periodsApiUrl.replace('{{houseId}}', this.houseId.toString());

        const urlParams: string = this.formatUrlParams(start, end);
        if (urlParams.length) {
            endpoint += `?${urlParams}`;
        }

        return HttpClient.get(endpoint).then((response: unknown) => {
            const periods: Period[] = response as Period[];

            if (
                !Array.isArray(periods) ||
                (periods.length === 0 && this._state.periods.length === 0)
            ) {
                return;
            }

            this._state.periods = this._state.periods.filter(
                (period: Period): boolean =>
                    periods.findIndex(
                        (newPeriod: Period): boolean => newPeriod.id === period.id
                    ) === -1
            );
            this._state.periods.push(...periods);
            document.dispatchEvent(new Event(calendarEvents.calendarUpdated));
        });
    }

    public fetchBookedPeriods(start: Date | null = null, end: Date | null = null): Promise<any> {
        if (this.houseId === null) {
            return Promise.resolve();
        }

        let endpoint: string = this.bookedPeriodsApiUrl.replace(
            '{{houseId}}',
            this.houseId.toString()
        );

        const urlParams: string = this.formatUrlParams(start, end);
        if (urlParams.length) {
            endpoint += `?${urlParams}`;
        }

        return HttpClient.get(endpoint).then((response: unknown) => {
            const bookedPeriods: BookedPeriod[] = response as BookedPeriod[];

            if (bookedPeriods.length === 0 && this._state.bookedPeriods.length === 0) {
                return;
            }

            this._state.bookedPeriods = this._state.bookedPeriods.filter(
                (bookedPeriod: BookedPeriod): boolean =>
                    bookedPeriods.findIndex(
                        (newPeriod: BookedPeriod): boolean => bookedPeriod.id === newPeriod.id
                    ) === -1
            );
            this._state.bookedPeriods.push(...bookedPeriods);
            document.dispatchEvent(new Event(calendarEvents.calendarUpdated));
        });
    }

    refresh(): Promise<any> {
        this._state.blockedDates = [];
        this._state.periods = [];

        return Promise.all([this.fetchPeriods(), this.fetchBlocks(), this.fetchBookedPeriods()]);
    }

    private formatUrlParams(start: Date | null = null, end: Date | null = null): string {
        const urlParams: string[] = [];
        if (start !== null) {
            const stringDate = formatDateForUrl(start);
            urlParams.push(`start=${stringDate}`);
        }

        if (end !== null) {
            const stringDate = formatDateForUrl(end);
            urlParams.push(`end=${stringDate}`);
        }

        return urlParams.join('&');
    }
}

export { PeriodStore };
export default new PeriodStore();
